// Customer-added text boxes (the "+" above the fields in the editor).
//
// A custom box is described exactly like a template field (left/top/width in
// the template's natural canvas pixels, fontFamily, fontSize, fill, ...), so
// every renderer draws it with the same code as the designed fields. It is
// stored on the cart / order item inside fieldValues:
//   { fieldId: 'custom-…', value, customField: { fontFamily, fontSize, fill,
//     left, top, width, textAlign, direction, lineHeight, label } }
// (the backend whitelists these in utils/customField.js). Shared by the
// editor and the download page; admin/src/utils has a copy.

export const CUSTOM_PREFIX = 'custom-';
const KEYS = ['fontFamily', 'fontSize', 'fill', 'left', 'top', 'width', 'textAlign', 'direction', 'lineHeight', 'label'];

export const isCustomId = (id) => String(id || '').startsWith(CUSTOM_PREFIX);

export function newCustomId() {
  return `${CUSTOM_PREFIX}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

// What gets saved with the cart / order item.
export function customFieldPayload(f) {
  const out = {};
  for (const k of KEYS) if (f[k] !== undefined && f[k] !== null && f[k] !== '') out[k] = f[k];
  return out;
}

// A field object the renderers understand.
export function customFieldShape(id, def) {
  return {
    ...def,
    _id: id,
    custom: true,
    editable: true,
    multiLine: false,
    originX: 'left',
    originY: 'top',
    textAlign: def.textAlign || 'center',
    direction: def.direction || 'rtl',
    lineHeight: def.lineHeight || 1.1,
    label: def.label || 'Added text',
  };
}

// Custom boxes saved on an order item's fieldValues.
export function customFieldsFromValues(fieldValues) {
  return (fieldValues || [])
    .filter(fv => isCustomId(fv.fieldId) && fv.customField && fv.customField.fontFamily)
    .map(fv => customFieldShape(String(fv.fieldId), fv.customField));
}

const hasHebrew = (s) => /[֐-׿]/.test(String(s || ''));
const median = (arr) => {
  const a = arr.filter(n => Number(n) > 0).map(Number).sort((x, y) => x - y);
  return a.length ? a[Math.floor(a.length / 2)] : 0;
};

// A new box: centred on the template, as wide as 70% of it, at the size the
// template typically uses for the chosen font.
export function makeCustomField({ text, fontFamily, fill, fields, canvasW, canvasH }) {
  // Template fields are often drawn scaled (scaleY 3-7x), so compare the size
  // they actually show at.
  const shown = (f) => (Number(f.fontSize) || 0) * (Number(f.scaleY) || 1);
  const sameFont = fields.filter(f => f.fontFamily === fontFamily).map(shown);
  const fontSize = Math.round(median(sameFont) || median(fields.map(shown)) || Math.max(18, canvasW / 22));
  const width = Math.round(canvasW * 0.7);
  return customFieldShape(newCustomId(), {
    fontFamily,
    fontSize,
    fill,
    width,
    left: Math.round((canvasW - width) / 2),
    top: Math.round(canvasH * 0.46 - fontSize / 2),
    textAlign: 'center',
    direction: hasHebrew(text) ? 'rtl' : 'ltr',
    lineHeight: 1.1,
  });
}
